import { ROOM_LABEL } from '../../stories/StoryOverlay';
import { isPlaceholderPlace } from '../../../utils/placeLabel';

/**
 * The Portal page's words and numbers, kept out of the JSX so they can be tested on their own.
 * Design handoff: ~/Downloads/design_handoff_portal_page/README.md (Sept 2026).
 */

const d = (iso) => {
  const x = iso ? new Date(iso) : null;
  return x && !Number.isNaN(x.getTime()) ? x : null;
};

/** `WED SEP 23, 1:14 AM` */
export function momentLabel(iso) {
  const x = d(iso);
  if (!x) return '';
  const day = x.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).replace(/,/g, '');
  const time = x.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day}, ${time}`.toUpperCase();
}

/** `WED SEP 23, 1:14 AM – FRI SEP 25, 11:00 PM` */
export function windowLine(startIso, endIso) {
  const a = momentLabel(startIso);
  const b = momentLabel(endIso);
  if (a && b) return `${a} – ${b}`;
  return a || b || '';
}

/** `ENDS IN 1 DAY 21 HR` · `ENDS IN 3 HR 12 MIN` · `ENDS IN 8 MIN` · `ENDED` */
export function endsIn(endIso, now = Date.now()) {
  const x = d(endIso);
  if (!x) return '';
  const ms = x.getTime() - now;
  if (ms <= 0) return 'ENDED';
  const mins = Math.floor(ms / 60000);
  const days = Math.floor(mins / 1440);
  const hrs = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  if (days > 0) return `ENDS IN ${days} ${days === 1 ? 'DAY' : 'DAYS'} ${hrs} HR`;
  if (hrs > 0) return `ENDS IN ${hrs} HR ${m} MIN`;
  return `ENDS IN ${Math.max(m, 1)} MIN`;
}

/** `Fri 11:00 PM` — for the result copy ("open until Fri 11:00 PM"). */
export function shortEnd(endIso) {
  const x = d(endIso);
  if (!x) return '';
  const day = x.toLocaleDateString('en-US', { weekday: 'short' });
  const time = x.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day} ${time}`;
}

export function firstName(name) {
  const n = (name || '').trim();
  return n ? n.split(/\s+/)[0] : 'The host';
}

/** Every room the Portal went to, in the server's vocabulary. */
export function roomsOf(signal) {
  const raw = Array.isArray(signal?.audiences) && signal.audiences.length
    ? signal.audiences
    : [signal?.signalAudience || 'friends'];
  const out = [];
  raw.forEach((r) => {
    const k = r === 'town_square' ? 'public' : r;
    if (k && !out.includes(k)) out.push(k);
  });
  return out;
}

/** `PUBLIC` · `FRIENDS + CLUB` — the chip on the slab. */
export function roomLabel(rooms) {
  const list = Array.isArray(rooms) ? rooms : roomsOf(rooms);
  return list.map((r) => ROOM_LABEL[r] || String(r).toUpperCase()).join(' + ') || 'FRIENDS';
}

/** The picture behind the page: the clip's poster, or the photo itself. */
export function backdropUrl(media) {
  if (!media) return null;
  if (media.kind === 'image') return media.mediaUrl || media.thumbnailUrl || null;
  return media.thumbnailUrl || null;
}

export function placeOf(signal) {
  if (!signal) return null;
  const city = [signal.primaryCityLabel, signal.locationLabel].find((c) => c && !isPlaceholderPlace(c)) || '';
  if (signal.venueName) return { name: signal.venueName, address: signal.venueAddress || city };
  if (city) return { name: city, address: '' };
  return null;
}

export function mapsUrl(signal) {
  if (!signal) return null;
  if (signal.latitude != null && signal.longitude != null && signal.venueName) {
    return `https://www.google.com/maps/search/?api=1&query=${signal.latitude},${signal.longitude}`;
  }
  const place = placeOf(signal);
  if (!place) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([place.name, place.address].filter(Boolean).join(', '))}`;
}

/** Which of the page's four states the viewer is in. */
export function viewFor(signal) {
  if (!signal) return null;
  if (signal.isMine) return 'host';
  if (signal.viewerState === 'accepted') return 'guest_in';
  if (signal.viewerState === 'applied_pending') return 'guest_asked';
  if (signal.viewerState === 'not_this_one') return 'not_this_one';
  return 'guest_open';
}

/** The exit zone: the red button and the sentence beside it. Null when there is no way out. */
export function exitFor(view, hostName) {
  const host = firstName(hostName);
  switch (view) {
    case 'host':
      return { kind: 'delete', label: 'DELETE PORTAL', copy: 'Everyone in it loses access and it comes off the wall. This can’t be undone.' };
    case 'guest_in':
      return { kind: 'leave', label: 'LEAVE PORTAL', copy: `You lose the address and the guest list. ${host} sees one fewer in.` };
    case 'guest_asked':
      return { kind: 'withdraw', label: 'WITHDRAW REQUEST', copy: `${host} won’t see your ask anymore. You can ask again while it’s open.` };
    default:
      return null;
  }
}

/** The confirm behind each red button. */
export function confirmFor(kind, hostName, personName = null) {
  const host = firstName(hostName);
  switch (kind) {
    case 'delete':
      return { title: 'DELETE PORTAL', message: 'Delete this portal? Everyone in it loses access and it disappears from the feed. This cannot be undone.', confirmLabel: 'DELETE', cancelLabel: 'KEEP' };
    case 'leave':
      return { title: 'LEAVE PORTAL', message: `Leave ${host}’s portal? You’ll lose the address and who’s coming. You can get back in while it’s open.`, confirmLabel: 'LEAVE', cancelLabel: 'STAY' };
    case 'withdraw':
      return { title: 'WITHDRAW REQUEST', message: `Take back your ask to join ${host}’s portal?`, confirmLabel: 'WITHDRAW', cancelLabel: 'KEEP IT' };
    case 'remove':
      return { title: 'REMOVE FROM PORTAL', message: `Remove ${personName || 'this person'} from your portal? They’ll lose access.`, confirmLabel: 'REMOVE', cancelLabel: 'KEEP' };
    default:
      return null;
  }
}

/** What replaces the action area once they've left, withdrawn or deleted. */
export function resultFor(kind, hostName, endIso) {
  const host = firstName(hostName);
  const until = shortEnd(endIso);
  switch (kind) {
    case 'left':
      return { chip: 'YOU LEFT', title: 'YOU LEFT THIS PORTAL.', body: `${host}’s portal is open${until ? ` until ${until}` : ''}.`, rejoin: 'I’M DOWN AGAIN' };
    case 'withdrawn':
      return { chip: 'WITHDRAWN', title: 'REQUEST WITHDRAWN.', body: `${host} won’t see it. The portal is open${until ? ` until ${until}` : ''}.`, rejoin: 'ASK AGAIN' };
    case 'deleted':
      return { chip: 'DELETED', title: 'PORTAL DELETED.', body: 'It’s off the wall and everyone in it has lost access.', rejoin: null };
    default:
      return null;
  }
}
